import { useCallback, useEffect, useMemo, useState } from 'react';
import { calmTokens as t } from '../../shared/tokens';
import { importService, labelService } from '../../services/api-secure';
import { IconAlert, IconBank, IconCheck, IconPlus, IconSparkle } from '../../shared/icons';
import CsvDropZone from './bank/CsvDropZone';
import ImportPreviewTable from './bank/ImportPreviewTable';
import ImportSummary, { StatsRow } from './bank/ImportSummary';
import ImportBatchList from './bank/ImportBatchList';
import LabelRulesTable from './bank/LabelRulesTable';
import LearnFlow from './bank/LearnFlow';
import { Card, PillButton } from './bank/ui';

function Notice({ children, warn }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        padding: '10px 14px',
        borderRadius: 12,
        fontSize: 12,
        lineHeight: 1.45,
        color: warn ? t.warn : t.textDim,
        background: warn ? 'rgba(253,224,71,0.06)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${warn ? 'rgba(253,224,71,0.25)' : t.border}`,
        marginBottom: 14,
      }}
    >
      <IconAlert size={14} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{children}</span>
    </div>
  );
}

/**
 * Section « Banque & import » du Paramétrage : import CSV avec aperçu
 * éditable, historique/annulation, règles de libellé et apprentissage.
 */
export default function BankImportSection({ showFeedback }) {
  const [status, setStatus] = useState(null);
  const [rules, setRules] = useState([]);
  const [batches, setBatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addingRule, setAddingRule] = useState(false);

  // Import
  const [phase, setPhase] = useState('idle'); // idle | analyzing | preview | committing | done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [edits, setEdits] = useState(new Map());
  const [lastBatch, setLastBatch] = useState(null);

  const loadRules = useCallback(async () => {
    try {
      setRules(await labelService.getRules());
    } catch (error) {
      console.error('Rules load error:', error);
    }
  }, []);

  const loadBatches = useCallback(async () => {
    try {
      setBatches(await importService.getBatches());
    } catch (error) {
      console.error('Batches load error:', error);
    }
  }, []);

  useEffect(() => {
    importService.getStatus().then(setStatus).catch(() => setStatus({ llmConfigured: false }));
    loadRules();
    loadBatches();
  }, [loadRules, loadBatches]);

  const llmReady = status?.llmConfigured !== false;

  const resetImport = () => {
    setPhase('idle');
    setFile(null);
    setPreview(null);
    setEdits(new Map());
    setLastBatch(null);
  };

  const analyze = async (f) => {
    setFile(f);
    setPhase('analyzing');
    setPreview(null);
    setEdits(new Map());
    try {
      const p = await importService.analyzeCsv(f);
      setPreview(p);
      setPhase('preview');
      if (p.warnings?.length) {
        showFeedback(`${p.warnings.length} avertissement(s) sur le fichier, voir l'aperçu.`, 'info');
      }
    } catch (error) {
      console.error('Analyze error:', error);
      showFeedback(error.message || 'Analyse impossible.', 'error');
      setPhase('idle');
      setFile(null);
    }
  };

  const onEdit = (rowIndex, value) => {
    setEdits((prev) => {
      const next = new Map(prev);
      const row = preview.rows.find((r) => r.rowIndex === rowIndex);
      const same = row && value.label === (row.label ?? '') && value.emoji === (row.emoji ?? '');
      if (same) next.delete(rowIndex); else next.set(rowIndex, value);
      return next;
    });
  };

  const commit = async () => {
    if (!preview) return;
    const overrides = [...edits].map(([rowIndex, v]) => ({ rowIndex, label: v.label, emoji: v.emoji }));
    const invalid = overrides.find((o) => !o.label.trim() && !o.emoji.trim());
    if (invalid) {
      showFeedback('Un libellé modifié est vide.', 'error');
      return;
    }
    setPhase('committing');
    try {
      const res = await importService.commit(preview.previewId, overrides);
      setLastBatch(res.batch);
      setPhase('done');
      showFeedback(`Import terminé : ${res.batch.stats.created} nouvelle(s), ${res.batch.stats.matched} rapprochée(s).`, 'success');
      loadBatches();
      loadRules();
    } catch (error) {
      console.error('Commit error:', error);
      showFeedback(error.message || 'Import impossible.', 'error');
      setPhase(error.code === 'PREVIEW_EXPIRED' ? 'idle' : 'preview');
      if (error.code === 'PREVIEW_EXPIRED') {
        setPreview(null);
        setFile(null);
      }
    }
  };

  const cancelBatch = async (batchId) => {
    if (!window.confirm('Annuler cet import ? Les opérations créées seront supprimées et les rapprochements défaits.')) return;
    setBusy(true);
    try {
      const r = await importService.cancelBatch(batchId);
      showFeedback(`Import annulé : ${r.deleted} opération(s) supprimée(s), ${r.unmatched} dé-pointée(s).`, 'success');
      if (lastBatch && lastBatch._id === batchId) setLastBatch(r.batch);
      loadBatches();
    } catch (error) {
      console.error('Cancel error:', error);
      showFeedback(error.message || 'Annulation impossible.', 'error');
    }
    setBusy(false);
  };

  const ruleAction = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) showFeedback(successMsg, 'success');
      await loadRules();
    } catch (error) {
      console.error('Rule error:', error);
      showFeedback(error.message || 'Action impossible.', 'error');
    }
    setBusy(false);
  };

  const reviewCount = useMemo(
    () => preview?.rows.filter((r) => r.action === 'create' && (r.labelStatus === 'llm' || r.labelStatus === 'unmatched') && !edits.has(r.rowIndex)).length || 0,
    [preview, edits]
  );

  const importBusy = phase === 'analyzing' || phase === 'committing';

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12, padding: '0 4px', flexWrap: 'wrap' }}>
        <h2 style={{ fontFamily: t.fontDisp, fontSize: 18, fontWeight: 500, margin: 0, letterSpacing: -0.3, color: t.text, display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconBank size={15} style={{ color: t.accent }} />
          Banque &amp; import
        </h2>
        <span style={{ fontSize: 11, color: t.textMute }}>
          Export CSV Caisse d’Épargne · libellés devinés par règles, apprentissage et IA
        </span>
      </div>

      {/* Import CSV */}
      <Card
        title="Importer un export"
        subtitle="Analyse d'abord, tu vérifies les libellés, puis tu importes. Rien n'est écrit avant « Importer »."
        action={status?.model && llmReady ? (
          <span style={{ fontSize: 11, color: t.textMute, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <IconSparkle size={11} /> {status.model}{status.provider === 'agent' ? ' · abonnement Claude' : ' · API'}
          </span>
        ) : null}
      >
        {!llmReady && (
          <Notice warn>
            L’import a besoin d’un accès à Claude côté serveur : une clé ANTHROPIC_API_KEY, ou LLM_PROVIDER=agent avec une session « claude login ». Sans cela, seuls l’apprentissage et les règles fonctionnent.
          </Notice>
        )}

        {phase === 'done' ? (
          <ImportSummary batch={lastBatch} onCancel={cancelBatch} onNew={resetImport} busy={busy} />
        ) : (
          <>
            <CsvDropZone onFile={analyze} busy={phase === 'analyzing'} file={file} disabled={!llmReady || importBusy} />

            {preview && phase !== 'analyzing' && (
              <div style={{ marginTop: 16 }}>
                {preview.warnings?.length > 0 && (
                  <Notice>
                    {preview.warnings.slice(0, 5).join(' · ')}
                    {preview.warnings.length > 5 ? ` · +${preview.warnings.length - 5}` : ''}
                  </Notice>
                )}
                <div style={{ marginBottom: 14 }}>
                  <StatsRow stats={preview.stats} llm={preview.llm} />
                </div>
                <ImportPreviewTable rows={preview.rows} edits={edits} onEdit={onEdit} onReset={() => setEdits(new Map())} />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
                  <PillButton
                    primary
                    icon={<IconCheck size={13} />}
                    onClick={commit}
                    disabled={importBusy || (preview.stats.created === 0 && preview.stats.matched === 0)}
                  >
                    {phase === 'committing' ? 'Import en cours…' : `Importer ${preview.stats.created} nouvelle${preview.stats.created > 1 ? 's' : ''}${preview.stats.matched ? ` + pointer ${preview.stats.matched}` : ''}`}
                  </PillButton>
                  <PillButton onClick={resetImport} disabled={importBusy}>Annuler</PillButton>
                  {reviewCount > 0 && (
                    <span style={{ fontSize: 11, color: t.warn }}>
                      {reviewCount} libellé{reviewCount > 1 ? 's' : ''} à vérifier (tu peux importer quand même, ils resteront marqués)
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: t.textMute, marginLeft: 'auto' }}>
                    Aperçu valable jusqu’à {new Date(preview.expiresAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Historique */}
      <Card title="Historique des imports" subtitle="Un import peut être annulé : ses opérations sont supprimées et les rapprochements défaits.">
        <ImportBatchList batches={batches} onCancel={cancelBatch} busy={busy} />
      </Card>

      {/* Apprentissage */}
      <Card
        title="Apprendre mes libellés"
        subtitle="Dépose un export d'une période déjà saisie à la main : l'app relie chaque ligne à ta saisie et te propose des règles."
      >
        <LearnFlow onFeedback={showFeedback} onRulesChanged={loadRules} />
      </Card>

      {/* Règles */}
      <Card
        title="Règles de libellé"
        subtitle="Appliquées avant le classifieur et Claude. Une règle que tu crées ou modifies l'emporte toujours."
        action={(
          <PillButton small primary icon={<IconPlus size={12} />} onClick={() => setAddingRule(true)} disabled={busy || addingRule}>
            Nouvelle règle
          </PillButton>
        )}
      >
        <LabelRulesTable
          rules={rules}
          busy={busy}
          adding={addingRule}
          onAddingChange={setAddingRule}
          onCreate={(form) => ruleAction(async () => {
            const r = await labelService.createRule({ ...form, applyToExisting: true });
            if (r.applied) showFeedback(`${r.applied} opération(s) renommée(s).`, 'success');
          }, 'Règle créée.')}
          onUpdate={(id, form) => ruleAction(() => labelService.updateRule(id, form), 'Règle modifiée.')}
          onDelete={(id) => ruleAction(() => labelService.deleteRule(id), 'Règle supprimée.')}
        />
      </Card>
    </div>
  );
}
